import { generateAttSessions } from '@client/utils/document-report';

import { getDateObject } from '@common/utils/dates';

/**
 * Builds a report object for pitches from a response array.
 * @param {Array} response - Array of activity data objects.
 * @returns {Object} An object containing the structured pitch report data.
 */
export const buildPitchReport = (response) => {
  if (!response) {
    return;
  }

  const slideData = generateAttSessions(response);
  const pitches = {};

  // Iterate over each data entry to process and categorize it.
  response.forEach((data) => {
    // Create or update the pitch object.
    const pitch = createOrUpdatePitch(data, pitches);

    // Define handlers for different activity types.
    const actionHandlers = {
      'tracked-attachment': processAttachmentTracked,
      'viewerlaunched-attachmentview': processAttachmentViewerLaunched,
      'slideviewed-attachmentslideview': processAttachmentSlideViewed,
      'downloaded-attachmentdownload': processAttachmentDownloaded
    };

    // Construct the key to find the correct handler.
    const handlerKey = `${data.verb}-${data.objecttype}`;
    const handler = actionHandlers[handlerKey];

    // If a handler exists for the activity, call it.
    if (handler) {
      handler(data, pitch, slideData);
    }
  });

  return pitches;
};

/**
 * Creates or updates a pitch object within the pitches collection.
 * @param {Object} data - The activity data for a single pitch.
 * @param {Object} pitches - The collection of all pitches.
 * @returns {Object|null} The updated pitch object, or null if no msglink is present.
 */
const createOrUpdatePitch = (data, pitches) => {
  if (!data.msglink) {
    return null;
  }

  let pitch = pitches[data.msglink];

  // If the pitch doesn't exist, initialize it.
  if (!pitch) {
    pitch = pitches[data.msglink] = {
      msgids: {},
      atts: {},
      recips: {},
      id: data.msglink,
      msglink: data.msglink,
      msgsubject: data.msgsubject,
      to: data.to,
      cc: data.cc,
      itemtype: 'pitch',
      lastactivitydate: getDateObject(data.utcmsgtime),
      splitsend: !!data.disablesplitsend
    };
  }

  // Update pitch properties based on the 'sent' verb.
  if (data.verb === 'sent') {
    pitch.msgtime = pitch.msgtime || getDateObject(data.utcmsgtime);
    pitch.lastactivitydate = pitch.lastactivitydate || pitch.msgtime;
    pitch.msgids[data.msgid] = pitch.msgids[data.msgid] || data.utcmsgtime;
  }

  // Add recipient to the pitch if it doesn't already exist.
  if (data.recipient && !pitch.recips[data.recipient]) {
    pitch.recips[data.recipient] = { msgid: data.msgid };
  }

  return pitch;
};

/**
 * Processes the 'tracked' activity for an attachment.
 * @param {Object} data - The activity data for the tracked attachment.
 * @param {Object} pitch - The pitch object to update with tracking information.
 */
const processAttachmentTracked = (data, pitch) => {
  // Destructure necessary fields from data.
  const { objectid: id, note, recipient } = data;
  const { ext, filename, filesize, hash, ms, session, slidecount = 0 } = note;

  // Ensure recipient's attachment data structure is initialized.
  pitch.recips[recipient].atts = pitch.recips[recipient].atts || {};
  pitch.recips[recipient].atts[hash] = pitch.recips[recipient].atts[hash] || {
    dlcount: 0,
    viewcount: 0,
    slideviewcount: 0,
    filename,
    filesize
  };

  // Initialize or update the attachment data.
  pitch.atts[id] = pitch.atts[id] || {
    filename,
    filesize,
    hash,
    ext,
    slidecount,
    recipsarray: [],
    series: []
  };

  // Update slide count if the new count is higher.
  if (slidecount > pitch.atts[id].slidecount) {
    pitch.atts[id].slidecount = slidecount;
  }

  // Add recipient to the attachment's recipient array if not already present.
  const recipientIndex = pitch.atts[id].recipsarray.indexOf(recipient);
  if (recipientIndex === -1) {
    pitch.atts[id].recipsarray.push(recipient);
  }

  // Update slide data for each slide in the attachment.
  for (let i = 0; i < slidecount; i++) {
    updateSlideData(pitch, id, i, ms / 1000, session, recipientIndex);
  }
};

/**
 * Processes the 'viewerlaunched' activity for an attachment view.
 * @param {Object} data - The activity data for the attachment view.
 * @param {Object} pitch - The pitch object to update with view information.
 */
const processAttachmentViewerLaunched = (data, pitch, slideData) => {
  // Destructure necessary fields from data.
  const { note, recipient, utcts } = data;
  const sessionKey = `${note.session}-id`;

  // Increment the view count for the attachment.
  pitch.recips[recipient].atts[note.hash].viewcount++;

  // Update the last activity date if this activity is more recent.
  const date = getDateObject(utcts);
  if (date > pitch.lastactivitydate) {
    pitch.lastactivitydate = date;
  }

  // Update session data with the viewer launched information.
  updateSessionData(pitch, note.attachmentid, sessionKey, data, slideData);
};

/**
 * Processes the 'slideviewed' activity for an attachment slide view.
 * @param {Object} data - The activity data for the slide view.
 * @param {Object} pitch - The pitch object to update with slide view information.
 */
const processAttachmentSlideViewed = (data, pitch) => {
  // Destructure necessary fields from data.
  const { objectid: id, note, recipient } = data;

  // Increment the slide view count for the attachment.
  pitch.recips[recipient].atts[note.hash].slideviewcount++;

  // Update the last activity date if this activity is more recent.
  const date = getDateObject(data.utcts);
  if (date > pitch.lastactivitydate) {
    pitch.lastactivitydate = date;
  }

  // Calculate the slide index and update slide data accordingly.
  const slideNumber = parseInt(note.slide) - 1;
  const slideDuration = note.ms / 1000;
  const slideIndex = pitch.atts[id].slidecount - slideNumber - 1;
  const recipientIndex = pitch.atts[id].recipsarray.indexOf(recipient);

  updateSlideData(pitch, id, slideIndex, slideDuration, note.session, recipientIndex);
};

/**
 * Updates the slide data with the duration and session information.
 * @param {Object} pitch - The pitch object containing the attachment data.
 * @param {string} id - The ID of the attachment.
 * @param {number} slideIndex - The index of the slide within the attachment.
 * @param {number} slideDuration - The duration the slide was viewed.
 * @param {string} session - The session ID during which the slide was viewed.
 * @param {number} recipientIndex - The index of the recipient in the recipients array.
 */
const updateSlideData = (pitch, id, slideIndex, slideDuration, session, recipientIndex) => {
  // Ensure the slide data structure is initialized.
  pitch.atts[id].series[slideIndex] = pitch.atts[id].series[slideIndex] || {
    name: `Slide ${pitch.atts[id].slidecount - slideIndex}`,
    data: [],
    details: []
  };

  // Update the total view duration for the slide for this recipient.
  const slideData = pitch.atts[id].series[slideIndex].data;
  const recipientDataIndex = recipientIndex || 0;
  slideData[recipientDataIndex] = slideData[recipientDataIndex] || 0;
  slideData[recipientDataIndex] += slideDuration;
  slideData[recipientDataIndex] = Math.round(slideData[recipientDataIndex] * 10) / 10;

  // Record the detailed view duration for the slide in this session.
  const slideDetails = pitch.atts[id].series[slideIndex].details;
  slideDetails[session] = slideDetails[session] || [];
  slideDetails[session][recipientDataIndex] = slideDetails[session][recipientDataIndex] || 0;
  slideDetails[session][recipientDataIndex] += slideDuration;
};

/**
 * Updates the session data with viewer information.
 * @param {Object} pitch - The pitch object to update with session information.
 * @param {string} attachmentid - The ID of the attachment.
 * @param {string} sessionKey - The key representing the session.
 * @param {Object} data - The activity data containing the session information.
 * @param {Object} slideData - The slide data for the pitch.
 */
const updateSessionData = (pitch, attachmentid, sessionKey, data, slideData) => {
  // Initialize the sessions data structure if it doesn't exist.
  pitch.sessions = pitch.sessions || {};
  pitch.sessions[attachmentid] = pitch.sessions[attachmentid] || {};

  // Destructure necessary fields from data.
  const { ip, msghashid, note, recipient, utcts } = data;
  const { coords, filename: details, location, isp, useragent, ua } = note;
  const series =
    Object.values(slideData).find(
      (item) => item.msghashid === msghashid && item.note?.session === sessionKey.replace('-id', '')
    )?.series || [];

  // Update or initialize the session data.
  pitch.sessions[attachmentid][sessionKey] = pitch.sessions[attachmentid][sessionKey] || {
    msghashid,
    recipient,
    location,
    coords,
    isp,
    utcts,
    useragent,
    ua,
    details,
    series,
    ip,
    note,
    selected: true
  };
};

/**
 * Processes the 'downloaded' activity for an attachment download.
 * @param {Object} data - The activity data for the download.
 * @param {Object} pitch - The pitch object to update with download information.
 */
const processAttachmentDownloaded = (data, pitch) => {
  // Destructure necessary fields from data.
  const { msglink, note, recipient, utcts } = data;
  const { filename, filesize, hash } = note;

  // Return early if there is no msglink.
  if (!msglink) {
    return;
  }

  // Ensure the recipient's attachment data structure is initialized.
  pitch.recips[recipient].atts = pitch.recips[recipient].atts || {};
  pitch.recips[recipient].atts[hash] = pitch.recips[recipient].atts[hash] || {
    dlcount: 0,
    viewcount: 0,
    slideviewcount: 0,
    filename,
    filesize
  };

  // Increment the download count for the attachment.
  pitch.recips[recipient].atts[hash].dlcount++;

  // Update the last activity date if this activity is more recent.
  const date = getDateObject(utcts);
  if (date > pitch.lastactivitydate) {
    pitch.lastactivitydate = date;
  }
};
